(() => {
  const qs = (selector, scope = document) => scope.querySelector(selector);
  const qsa = (selector, scope = document) => [...scope.querySelectorAll(selector)];

  const header = qs('[data-header]');
  const progress = qs('.page-progress span');
  const onScroll = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    if (header) header.classList.toggle('is-scrolled', window.scrollY > 18);
    if (progress) progress.style.width = `${max > 0 ? Math.min(100, window.scrollY / max * 100) : 0}%`;
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: .12, rootMargin: '0px 0px -40px' });
  qsa('[data-reveal]').forEach((element) => observer.observe(element));

  const searchOverlay = qs('[data-search-overlay]');
  const mobileMenu = qs('[data-mobile-menu]');
  const setOpen = (element, open) => {
    if (!element) return;
    element.hidden = !open;
    document.body.classList.toggle('menu-open', open);
    if (open) setTimeout(() => qs('input', element)?.focus(), 50);
  };
  qs('[data-search-open]')?.addEventListener('click', () => setOpen(searchOverlay, true));
  qs('[data-search-close]')?.addEventListener('click', () => setOpen(searchOverlay, false));
  qs('[data-menu-open]')?.addEventListener('click', () => setOpen(mobileMenu, true));
  qs('[data-menu-close]')?.addEventListener('click', () => setOpen(mobileMenu, false));
  searchOverlay?.addEventListener('click', (event) => { if (event.target === searchOverlay) setOpen(searchOverlay, false); });
  qsa('a', mobileMenu).forEach((link) => link.addEventListener('click', () => setOpen(mobileMenu, false)));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      setOpen(searchOverlay, false);
      setOpen(mobileMenu, false);
    }
  });

  const toast = qs('.product-toast');
  let toastTimer;
  const showToast = (title) => {
    if (!toast) return;
    const label = qs('small', toast);
    if (label) label.textContent = title;
    toast.classList.add('show');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('show'), 4200);
  };
  const refreshCartCount = async () => {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    qsa('[data-cart-count]').forEach((count) => { count.textContent = cart.item_count; });
  };

  qsa('form[data-product-form]').forEach((form) => {
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = qs('[type="submit"]', form);
      if (!button || button.disabled || button.dataset.loading === 'true') return;
      button.dataset.loading = 'true';
      button.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) throw new Error('Unable to add item');
        const item = await response.json();
        await refreshCartCount();
        showToast(item.product_title || 'Product');
      } catch (error) {
        form.submit();
      } finally {
        button.dataset.loading = 'false';
        button.removeAttribute('aria-busy');
      }
    });
  });

  qsa('[data-product-section]').forEach((section) => {
    const quantity = qs('input[name="quantity"]', section);
    qs('[data-qty-minus]', section)?.addEventListener('click', () => { quantity.value = Math.max(1, Number(quantity.value || 1) - 1); });
    qs('[data-qty-plus]', section)?.addEventListener('click', () => { quantity.value = Number(quantity.value || 1) + 1; });
    const variant = qs('select[name="id"]', section);
    variant?.addEventListener('change', () => {
      const selected = variant.options[variant.selectedIndex];
      const available = selected.dataset.available !== 'false';
      const price = qs('[data-product-price]', section);
      if (price && selected.dataset.price) price.textContent = selected.dataset.price;
      const stickyPrice = qs('[data-sticky-product-price]', section);
      if (stickyPrice && selected.dataset.price) stickyPrice.textContent = selected.dataset.price;
      const addPrice = qs('[data-add-product-price]', section);
      if (addPrice) addPrice.textContent = available ? selected.dataset.price : '';
      const addButton = qs('[data-product-add-button]', section);
      const addLabel = qs('[data-add-label]', section);
      if (addButton) addButton.disabled = !available;
      if (addLabel) addLabel.textContent = available ? 'Add to cart' : 'Sold out';
      qsa('[data-buy-now]', section).forEach((button) => { button.disabled = !available; });
    });
    qsa('[data-variant-pill]', section).forEach((pill) => pill.addEventListener('click', () => {
      if (!variant || pill.disabled) return;
      variant.value = pill.dataset.variantId;
      qsa('[data-variant-pill]', section).forEach((item) => item.classList.toggle('is-active', item === pill));
      variant.dispatchEvent(new Event('change', { bubbles: true }));
    }));

    const gallery = qs('[data-product-gallery]', section);
    const slides = qsa('[data-gallery-slide]', section);
    const dots = qsa('[data-gallery-dot]', section);
    const setActiveDot = (index) => {
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };
    let galleryFrame;
    gallery?.addEventListener('scroll', () => {
      cancelAnimationFrame(galleryFrame);
      galleryFrame = requestAnimationFrame(() => {
        const center = gallery.scrollLeft + gallery.clientWidth / 2;
        let activeIndex = 0;
        let closest = Infinity;
        slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft + slide.offsetWidth / 2 - center);
          if (distance < closest) { closest = distance; activeIndex = index; }
        });
        setActiveDot(activeIndex);
      });
    }, { passive: true });
    dots.forEach((dot, index) => dot.addEventListener('click', () => {
      const slide = slides[index];
      if (!gallery || !slide) return;
      gallery.scrollTo({ left: slide.offsetLeft - (gallery.clientWidth - slide.clientWidth) / 2, behavior: 'smooth' });
    }));

    qsa('[data-buy-now]', section).forEach((buyButton) => buyButton.addEventListener('click', () => {
      const form = qs('form[data-product-form]', section);
      if (!form || buyButton.dataset.loading === 'true') return;
      const label = qs('span', buyButton);
      buyButton.dataset.loading = 'true';
      buyButton.classList.add('is-checkout-loading');
      buyButton.setAttribute('aria-busy', 'true');
      qsa('[data-buy-now]', section).forEach((button) => { button.disabled = true; });
      if (label) label.textContent = buyButton.dataset.loadingLabel;
      let returnTo = qs('input[name="return_to"]', form);
      if (!returnTo) {
        returnTo = document.createElement('input');
        returnTo.type = 'hidden';
        returnTo.name = 'return_to';
        form.appendChild(returnTo);
      }
      returnTo.value = '/checkout';
      window.setTimeout(() => form.submit(), 450);
    }));
  });
})();
